import { QuestionConditionEditor } from './QuestionConditionEditor';
/**
 * FormBuilder - Notion-style block-editor canvas for designing conversations.
 *
 * Top-level component for the form-first interview redesign. The wizard owns
 * the `FormField[]` source of truth and passes it in; the builder fires
 * `onChange` with a fresh array on every mutation (add, delete, reorder, edit).
 *
 * Layout: a single column of full-width `<FieldCard>` blocks. Each block
 * carries its OWN per-card `+` rail on the absolutely-positioned left edge
 * (used to insert directly below that block); the canvas itself only renders
 * a single round `<AddFieldButton variant="bottom">` at the very end of the
 * conversation for "append" - no inter-card rails.
 *
 * All definitions expose the five-family catalogue. New fields default to optional;
 * required status and answer-channel policy are independently editable.
 *
 * Edit mode: this component does not know whether its host is creating or
 * editing a campaign except through `committedImageKeys`. When the host
 * (`CreateInterviewForm` in `editSession` mode) is editing an existing
 * campaign, it passes the storage keys of every information-block image
 * already referenced by the SAVED schema. `handleDeleteField` checks that
 * set before firing the best-effort R2 delete for a removed information
 * block's image, so removing a card, or replacing its image, never deletes
 * media a saved campaign or in-flight session still points at. The create
 * flow passes nothing, leaving its delete-on-remove behavior unchanged.
 *
 * @module components/form-builder/FormBuilder
 */

import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import type { FieldType, FormField } from '../../types';
import { isQuestionConditionSource, projectQuestionConditionSource, questionConditionIssue } from '../../lib/question-conditions';
import { streamFormGeneration, type SseEvent, type SseProductResearch } from '../../lib/sse-consumer';
import { resolveGeneratedGif } from '../../lib/generated-gif';
import { GenerationRequestError } from '../../lib/generation-errors';
import { generateFieldId } from '../../lib/form-templates';
import { DEFAULT_BOOKABLE_WINDOWS } from '../../lib/integrations/calendar/default-windows';
import { getSupabase } from '../../lib/supabase';
import { offlineSessionWorkerUrl } from '../../lib/worker-urls';
import { FieldCard, type GuestBindingSources, type GuestProfileProperty } from './FieldCard';
import { AddFieldButton } from './AddFieldButton';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { cloneFormFields, createFieldByFormat, FORM_FIELD_CAPABILITIES, FORM_FIELD_LIMITS, GUEST_EMAIL_BINDING, GUEST_NAME_BINDING, validateFormFields, type FormFieldFormat } from '../../lib/form-capabilities';
import { applyDefaultGuestBindings } from '../../lib/guest-binding-defaults';
import { collectQuestionImageStorageKeys } from './useInformationBlockAbandonCleanup';
import { getConversationSectionBlocks, sectionConditionSources, type ConversationSection } from '../../lib/conversation-sections';
import { addBuilderSection, insertSectionFields, moveBuilderSection, moveSectionFields, reconcileBuilderSections } from './sections/section-editor';
import { DraggableSection } from './sections/DraggableSection';
import { useLayoutMotion } from './sections/useLayoutMotion';
import { BlockActionsMenu } from '../ui/BlockActionsMenu';
import { IconDots } from '@tabler/icons-react';
import './sections/sections.css';

/** Stable default for legacy hosts without persisted grouping. */
const NO_SECTIONS: ConversationSection[] = [];

/** Props accepted by `FormBuilder`. */
export interface FormBuilderProps {
  /** Brand and workspace policy context for one-question generation. */
  productResearch?: SseProductResearch;
  /** Whether call-only answer settings are relevant to this conversation. */
  callsEnabled?: boolean;
  /** Wraps file authoring content in its Drive connection gate. */
  fileDestination?: (content: React.ReactNode) => React.ReactNode;
  /** The fields to render. Treated as the source of truth. */
  fields: FormField[];
  /** Fired with the new array shape on every add/delete/edit/reorder. */
  onChange: (fields: FormField[]) => void;
  /** Persisted grouping metadata, separate from respondent steps. */
  sections?: ConversationSection[];
  /** Commit membership and field order together when the host supports sections. */
  onDefinitionChange?: (fields: FormField[], sections: ConversationSection[]) => void;
  /**
   * Whether the wizard is still streaming AI-generated cards in. While true
   * the canvas keeps `SKELETON_COUNT` total slots filled - real cards from
   * `fields` at the top, `<FieldCard loading>` placeholders in the trailing
   * slots - so each arriving real card replaces the skeleton at that slot in
   * place rather than every skeleton unmounting at once.
   */
  isGenerating?: boolean;
  /**
   * Owning workspace id. Threaded to information-block cards for their image
   * upload/caption/delete worker calls and to the best-effort R2 delete fired
   * when an information block is removed.
   */
  workspaceId?: string;
  /** Optional campaign goal, passed to the information-block caption request. */
  campaignGoal?: string;
  /**
   * Per-field publish issues keyed by field id, mapping to a short hint shown
   * in the card's marker `title`. Populated by the wizard's publish gate so
   * each unresolved information block shows a visual flag.
   */
  informationBlockIssues?: Map<string, string>;
  /**
   * Storage keys of info-block images already referenced by the SAVED
   * campaign (edit mode). Removing a block or image whose key is in this set
   * must NOT fire the best-effort R2 delete: the saved config (and in-flight
   * sessions) still point at that media, and cancelling the edit must leave
   * it intact. The create flow passes nothing, keeping behaviour identical.
   */
  committedImageKeys?: ReadonlySet<string>;
}

/** Short uuid fragment for an information block's programmatic name. */
const shortId = (): string => generateFieldId().replace(/[^a-z0-9]/gi, '').slice(0, 8);

/**
 * Build a fresh information-block field. Mirrors {@link createFreeFormExploreField}
 * defaults (`mustOrTry: 'try'`, `required: false`, empty label) and stamps a
 * unique programmatic name so the live-session bridge has a stable key.
 */
const createInformationBlockField = (): FormField => ({
  id: generateFieldId(),
  name: `information_block_${shortId()}`,
  label: '',
  required: false,
  mustOrTry: 'try',
  type: 'information',
  delivery: 'spoken',
});

/**
 * Build a fresh calendar-booking field with milestone-1 defaults stamped:
 * self-hosted, a 30 minute call, no buffer, 24 hours notice, a 14 day horizon,
 * and an editable Monday-Friday 09:00-17:00 availability. Constraints are
 * structured so a booking never depends on parsing prose. A unique programmatic
 * name gives the live-session bridge a stable key.
 */
const createCalendarBookingField = (): FormField =>
  ({
    id: generateFieldId(),
    name: `calendar_booking_${shortId()}`,
    label: '',
    required: false,
    mustOrTry: 'try',
    type: 'calendar_booking',
    hostMode: 'self',
    durationMinutes: 30,
    bufferMinutes: 0,
    minimumNoticeHours: 24,
    bookingHorizonDays: 14,
    bookableWindows: DEFAULT_BOOKABLE_WINDOWS.map((w) => ({ ...w })),
  }) as FormField;

/**
 * Build a fresh Free Form Explore field with the legacy-compatible defaults:
 * `mustOrTry: 'try'`, `required: false`, empty label.
 */
const createFreeFormExploreField = (): FormField => ({
  id: generateFieldId(),
  name: '',
  label: '',
  required: false,
  mustOrTry: 'try',
  type: 'free_form_explore',
});

/** Returns the field without its guest profile binding key (no `undefined` left behind). */
function withoutGuestBinding(field: FormField): FormField {
  if ((field.type !== 'email' && field.type !== 'name') || !('profileBinding' in field)) return field;
  const unbound = { ...field };
  delete unbound.profileBinding;
  return unbound;
}

/** The first question saved to each guest property, keyed by the property it holds. */
function guestBindingSources(fields: readonly FormField[]): GuestBindingSources {
  const sources: GuestBindingSources = {};
  for (const field of fields) {
    if ((field.type === 'email' || field.type === 'name') && field.profileBinding?.property === field.type && !sources[field.type]) {
      sources[field.type] = { id: field.id, label: field.label };
    }
  }
  return sources;
}

/** Inclusive bounds of one Information/question pair or standalone field. */
function fieldGroupBounds(fields: readonly FormField[], index: number): [number, number] {
  const field = fields[index];
  if (field?.informationFieldId && fields[index - 1]?.id === field.informationFieldId && fields[index - 1]?.type === 'information') return [index - 1, index];
  if (field?.type === 'information' && fields[index + 1]?.informationFieldId === field.id) return [index, index + 1];
  return [index, index];
}

/**
 * Top-level Notion-style canvas. Renders a single column of full-width field
 * cards with per-card hover rails on the left and a single round append "+"
 * at the bottom.
 */
export const FormBuilder: React.FC<FormBuilderProps> = ({
  productResearch,
  callsEnabled = true,
  fileDestination,
  fields,
  onChange,
  sections = NO_SECTIONS,
  onDefinitionChange,
  isGenerating = false,
  workspaceId,
  campaignGoal,
  informationBlockIssues,
  committedImageKeys,
}) => {
  // Always merge field edits against the LATEST fields, not a render-time
  // snapshot. The information-block editor fires two updates across an async
  // boundary (image keys on upload, then title/directive/caption ~2s later when
  // the AI caption lands). A captured-at-upload closure would merge the caption
  // onto the pre-upload field and silently wipe imageStorageKey/imageUrl, which
  // surfaced as "image disappeared" and then as an "Add an image" publish block.
  const fieldsRef = useRef(fields);
  fieldsRef.current = fields;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const pendingFocusRef = useRef<string | null>(null);
  const pendingSectionFocusRef = useRef<string | null>(null);
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const { capture, play } = useLayoutMotion(containerRef);
  const sectionsEnabled = Boolean(onDefinitionChange);
  const tonesRef = useRef(new Map<string, 'volt' | 'warmth' | 'flow'>());
  const toneFor = (id: string) => {
    if (!tonesRef.current.has(id)) tonesRef.current.set(id, (['volt', 'warmth', 'flow'] as const)[tonesRef.current.size % 3]);
    return tonesRef.current.get(id)!;
  };

  /** Every mutation keeps the saved field order and section membership in sync. */
  const commit = useCallback((next: FormField[], nextSections?: ConversationSection[]) => {
    capture();
    const grouping = nextSections ?? reconcileBuilderSections(fieldsRef.current, next, sectionsRef.current);
    fieldsRef.current = next;
    sectionsRef.current = grouping;
    if (onDefinitionChange) onDefinitionChange(next, grouping);
    else onChange(next);
  }, [capture, onChange, onDefinitionChange]);
  useLayoutEffect(() => {
    play();
    const id = pendingSectionFocusRef.current;
    if (!id) return;
    const target = Array.from(containerRef.current?.querySelectorAll<HTMLElement>('[data-motion-key]') ?? []).find(element => element.dataset.motionKey === `section-${id}`);
    const input = target?.querySelector<HTMLTextAreaElement>('[aria-label="Section title"]');
    input?.focus(); input?.select(); pendingSectionFocusRef.current = null;
  }, [fields, sections, play]);

  /** Focus the exact authored question without interpolating an ID into CSS. */
  const focusQuestion = useCallback((fieldId: string) => {
    const cards = containerRef.current?.querySelectorAll<HTMLElement>('[data-field-id]');
    const card = Array.from(cards ?? []).find(element => element.dataset.fieldId === fieldId);
    card?.querySelector<HTMLTextAreaElement>('[data-testid="field-card-label"]')?.focus();
  }, []);

  useLayoutEffect(() => {
    if (!pendingFocusRef.current) return;
    focusQuestion(pendingFocusRef.current);
    pendingFocusRef.current = null;
  }, [fields, focusQuestion]);

  /** Build the requested format, defaulting an untyped new question to Text. */
  const createFieldByType = useCallback((type?: FieldType, format?: FormFieldFormat): FormField => {
    if (format === 'calendar' || (!format && type === 'calendar_booking')) return createCalendarBookingField();
    if (format) return createFieldByFormat(format, fieldsRef.current);
    if (type === 'information') return createInformationBlockField();
    if (type && type !== 'free_form_explore') return createFieldByFormat(FORM_FIELD_CAPABILITIES.find((capability) => capability.type === type)!.format, fieldsRef.current);
    return type === 'free_form_explore' ? createFreeFormExploreField() : createFieldByFormat('text', fieldsRef.current);
  }, []);

  // One calendar block per campaign: computed once so the menu (via every
  // AddFieldButton) can hide the "Calendar" option when one already exists.
  const hasCalendarBlock = fields.some((f) => f.type === 'calendar_booking');

  /** Add a new field of the given type at `insertIndex` (default Text). */
  const handleAddField = useCallback(
    (insertIndex: number, type?: FieldType, format?: FormFieldFormat, sectionId?: string) => {
      try {
        const current = fieldsRef.current;
        const added = applyDefaultGuestBindings([createFieldByType(type, format)], current);
        const next = insertSectionFields(current, sectionsRef.current, insertIndex, added, sectionId);
        commit(next.fields, next.sections);
      } catch (error) {
        captureException(error as Error, 'FormBuilder.handleAddField', {
          insertIndex,
          fieldCount: fields.length,
        });
      }
    },
    [createFieldByType, fields, commit],
  );

  /** Buffer one complete block, resolve its optional GIF, then merge into the latest canvas. */
  const handleGenerateField = useCallback(async (insertIndex: number, description: string, signal: AbortSignal, sectionId?: string) => {
    const initial = fieldsRef.current;
    const anchorId = initial[insertIndex - 1]?.id;
    const { data: { session } } = await getSupabase().auth.getSession();
    if (signal.aborted) return;
    if (!session) throw new Error('A session is required to generate a question.');
    const generated: Extract<SseEvent, { type: 'field' }>[] = [];
    let completed = false;
    for await (const event of streamFormGeneration({
      scope: 'single_question', multimodalVersion: 1, template: 'custom',
      objective: description, campaignGoal,
      productResearch,
      existingFields: initial.map(({ label, type }) => ({ label, type })),
      conditionSources: initial.slice(0, insertIndex).map(projectQuestionConditionSource).filter(isQuestionConditionSource),
    }, signal, { accessToken: session.access_token })) {
      if (signal.aborted) return;
      if (event.type === 'error') {
        // The worker captured what it classified; keep only its public code.
        if (event.serverReported) throw new GenerationRequestError(event.code, { retryable: event.retryable });
        throw new Error(`Invalid question generation event (${event.code})`);
      }
      if (event.type === 'field') generated.push(event);
      if (event.type === 'done') completed = event.count === 1;
    }
    if (signal.aborted) return;
    const draft = generated[0];
    if (!completed || generated.length !== 1 || !draft
      || draft.field.type === 'calendar_booking' || draft.field.informationFieldId
      || validateFormFields([draft.field], { allowIncompleteResources: true }).some(issue => issue.reason !== 'invalid_condition')) {
      throw new Error('Generation did not produce one complete block.');
    }
    const field = draft.gifQuery ? await resolveGeneratedGif(draft.field, draft.gifQuery, signal) : draft.field;
    if (signal.aborted) return;
    const current = fieldsRef.current;
    const anchorIndex = anchorId ? current.findIndex(candidate => candidate.id === anchorId) : -1;
    // A deleted anchor cancels the insertion rather than moving it elsewhere.
    if (anchorId && anchorIndex < 0) throw new Error('The insertion point was removed.');
    const names = new Set(current.map(candidate => candidate.name));
    const baseName = field.name;
    let suffix = 2;
    while (names.has(field.name)) {
      const ending = `_${suffix++}`;
      field.name = `${baseName.slice(0, FORM_FIELD_LIMITS.identifier - ending.length)}${ending}`;
    }
    if (current.some(candidate => candidate.id === field.id)) field.id = generateFieldId();
    const position = anchorId ? fieldGroupBounds(current, anchorIndex)[1] + 1 : 0;
    if (field.showWhen && questionConditionIssue(field.showWhen, current.slice(0, position))) throw new Error('The condition source changed. Generate this question again.');
    if (sectionId && !sectionsRef.current.some(section => section.id === sectionId)) throw new Error('The section was removed.');
    const next = insertSectionFields(current, sectionsRef.current, position, applyDefaultGuestBindings([field], current), sectionId);
    pendingFocusRef.current = field.id;
    commit(next.fields, next.sections);
  }, [campaignGoal, commit, productResearch]);

  /**
   * Best-effort delete of an information block's R2 image when its card is
   * removed. Swallows failures (it is cleanup, not a user-facing operation);
   * a transport failure is captured as an unexpected recovered error.
   */
  const deleteInformationBlockImage = useCallback(
    async (storageKey: string) => {
      try {
        const {
          data: { session },
        } = await getSupabase().auth.getSession();
        const token = session?.access_token;
        if (!token) return;
        const response = await fetch(`${offlineSessionWorkerUrl()}/information-block/delete`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ workspaceId, imageStorageKey: storageKey }),
        });
        if (!response.ok) throw new Error(`Image deletion failed (${response.status})`);
      } catch (error) {
        captureException(
          error as Error,
          'FormBuilder.deleteInformationBlockImage',
          { workspaceId },
        );
      }
    },
    [workspaceId],
  );

  /** Merge a partial patch into the field with the given id. */
  const handleUpdateField = useCallback(
    (id: string, patch: Partial<FormField>) => {
      const current = fieldsRef.current;
      const previous = current.find(field => field.id === id);
      const next = current.map((field) => {
        if (field.id !== id) return field;
        const updated = { ...field, ...patch } as FormField;
        return updated.type !== field.type
          ? applyDefaultGuestBindings([updated], current.filter(candidate => candidate.id !== id))[0]
          : updated;
      });
      commit(next);

      if (!previous) return;
      const updated = next.find(field => field.id === id);
      const removedKeys = new Set<string>();
      if (previous.type === 'image_choice') {
        if (updated?.type === 'image_choice') {
          const retainedOptionIds = new Set(updated.options.map(option => option.id));
          for (const option of previous.options) {
            if (!retainedOptionIds.has(option.id) && option.image?.imageStorageKey) removedKeys.add(option.image.imageStorageKey);
          }
        } else {
          for (const option of previous.options) if (option.image?.imageStorageKey) removedKeys.add(option.image.imageStorageKey);
        }
      }
      if (previous.type === 'image_click_map' && updated?.type !== 'image_click_map' && previous.image?.imageStorageKey) {
        removedKeys.add(previous.image.imageStorageKey);
      }
      if (removedKeys.size) {
        const retainedKeys = new Set(collectQuestionImageStorageKeys(next));
        for (const storageKey of removedKeys) {
          if (!committedImageKeys?.has(storageKey) && !retainedKeys.has(storageKey)) void deleteInformationBlockImage(storageKey);
        }
      }
    },
    [committedImageKeys, deleteInformationBlockImage, commit],
  );

  /** Clone content with its question, or detach a copied question from its group. */
  const handleDuplicateField = useCallback((id: string) => {
    const current = fieldsRef.current;
    const index = current.findIndex(field => field.id === id);
    if (index < 0 || current[index].type === 'calendar_booking') return;
    const [, end] = fieldGroupBounds(current, index);
    const source = current[index].type === 'information' ? current.slice(index, end + 1) : [current[index]];
    // A single-question copy must never create a second guest email or name binding.
    const copies = cloneFormFields(source).map(withoutGuestBinding);
    if (current[index].informationFieldId) delete copies[0].informationFieldId;
    const names = new Set(current.map(field => field.name));
    for (const copy of copies) {
      const base = copy.name;
      let suffix = 2;
      while (names.has(copy.name)) {
        const ending = `_${suffix++}`;
        copy.name = `${base.slice(0, FORM_FIELD_LIMITS.identifier - ending.length)}${ending}`;
      }
      names.add(copy.name);
    }
    const next = insertSectionFields(current, sectionsRef.current, end + 1, copies, sectionsRef.current.find(section => section.fieldIds.includes(id))?.id);
    commit(next.fields, next.sections);
  }, [commit]);

  /**
   * Save one Email or Name answer to the matching guest property, or clear it.
   * Binding moves atomically within that property: the chosen question gains
   * it and any other question holding the same property loses it in the same
   * emitted array. The other property's binding is never touched.
   */
  const handleGuestBindingChange = useCallback((id: string, property: GuestProfileProperty, bound: boolean) => {
    const current = fieldsRef.current;
    if (bound && !current.some(field => field.id === id && field.type === property)) return;
    const binding = property === 'email' ? GUEST_EMAIL_BINDING : GUEST_NAME_BINDING;
    const next = current.map((field): FormField => {
      if (field.type !== 'email' && field.type !== 'name') return field;
      if (bound && field.id === id && field.type === property) return { ...field, profileBinding: { ...binding } };
      const holdsProperty = field.profileBinding?.property === property;
      return (bound && holdsProperty) || (!bound && field.id === id) ? withoutGuestBinding(field) : field;
    });
    fieldsRef.current = next;
    onChange(next);
  }, [onChange]);

  /** Remove the field with the given id. */
  const handleDeleteField = useCallback(
    (id: string) => {
      const current = fieldsRef.current;
      const removed = current.find((f) => f.id === id);
      const next = current.filter((f) => f.id !== id && !(removed?.type === 'information' && f.informationFieldId === id));
      commit(next);
      // When an information block is removed, free its uploaded image so an
      // abandoned upload does not orphan an R2 object. Only fires when the
      // block actually had an image, and never for images the SAVED campaign
      // still references (edit mode: the saved config and in-flight sessions
      // keep rendering them).
      if (removed) {
        const removedFields = current.filter(field => !next.some(candidate => candidate.id === field.id));
        const retainedKeys = new Set(collectQuestionImageStorageKeys(next));
        for (const storageKey of collectQuestionImageStorageKeys(removedFields)) {
          if (!committedImageKeys?.has(storageKey) && !retainedKeys.has(storageKey)) {
            void deleteInformationBlockImage(storageKey);
          }
        }
      }
    },
    [committedImageKeys, deleteInformationBlockImage, commit],
  );

  /** Reorder via drag-drop OR Alt+ArrowUp/Down. Bounds-checked. */
  const handleMoveField = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const current = fieldsRef.current;
      if (
        dragIndex < 0 ||
        dragIndex >= current.length ||
        hoverIndex < 0 ||
        hoverIndex >= current.length ||
        dragIndex === hoverIndex
      ) {
        return dragIndex;
      }
      const [start, end] = fieldGroupBounds(current, dragIndex);
      const [targetStart, targetEnd] = fieldGroupBounds(current, hoverIndex);
      if (start === targetStart) return dragIndex;
      if (sectionsEnabled) {
        const target = sectionsRef.current.find(section => section.fieldIds.includes(current[hoverIndex].id));
        const beforeId = start < targetStart ? current[targetEnd + 1]?.id : current[targetStart].id;
        const result = moveSectionFields(current, sectionsRef.current, current[dragIndex].id, target?.id, beforeId);
        commit(result.fields, result.sections);
        return result.fields.findIndex(field => field.id === current[dragIndex].id);
      }
      const next = [...current];
      const moved = next.splice(start, end - start + 1);
      const destination = start < targetStart ? targetEnd + 1 - moved.length : targetStart;
      next.splice(destination, 0, ...moved);
      commit(next);
      return next.findIndex(field => field.id === current[dragIndex].id);
    },
    [commit, sectionsEnabled],
  );

  /** Reuse the single attached question or add it immediately after its content. */
  const handleAddReaction = useCallback((fieldId: string) => {
    const current = fieldsRef.current;
    const position = current.findIndex(field => field.id === fieldId && field.type === 'information');
    if (position < 0) return;
    const existing = current.find(field => field.informationFieldId === fieldId);
    if (existing) { focusQuestion(existing.id); return; }
    const reaction = createFieldByFormat('text', current);
    reaction.informationFieldId = fieldId;
    const updated = current.map((field, index) => index === position ? { ...field, responseMode: 'passive' } as FormField : field);
    const next = insertSectionFields(updated, sectionsRef.current, position + 1, [reaction], sectionsRef.current.find(section => section.fieldIds.includes(fieldId))?.id);
    pendingFocusRef.current = reaction.id;
    commit(next.fields, next.sections);
  }, [focusQuestion, commit]);

  /** The existing plus divides its owner at the requested position. */
  const handleAddSection = useCallback((index: number, ownerId?: string) => {
    const id = generateFieldId();
    const next = addBuilderSection(fieldsRef.current, sectionsRef.current, index, id, ownerId);
    pendingSectionFocusRef.current = id;
    commit(next.fields, next.sections);
  }, [commit]);

  /** Exact release placement keeps the canvas still while choosing a destination. */
  const handleDropField = useCallback((fieldId: string, targetId: string, edge: 'above' | 'below') => {
    const current = fieldsRef.current;
    const index = current.findIndex(field => field.id === targetId);
    if (index < 0) return;
    const [start, end] = fieldGroupBounds(current, index);
    const owner = sectionsRef.current.find(section => section.fieldIds.includes(targetId));
    const next = moveSectionFields(current, sectionsRef.current, fieldId, owner?.id, edge === 'above' ? current[start].id : current[end + 1]?.id);
    commit(next.fields, next.sections);
  }, [commit]);

  /** Whole groups move without recreating their fields or answers. */
  const handleMoveSection = useCallback((from: number, to: number) => {
    const next = moveBuilderSection(fieldsRef.current, sectionsRef.current, from, to);
    commit(next.fields, next.sections);
  }, [commit]);

  // The question saved to each guest property, shown on every Email and Name card.
  const guestSources = guestBindingSources(fields);

  /** Render a field with shared mutations and the bounds of its authored group. */
  const renderField = (field: FormField, index: number, section?: ConversationSection) => {
    const [groupStartIndex, groupEndIndex] = fieldGroupBounds(fields, index);
    return <FieldCard
      key={field.id}
      field={field}
      earlierFields={fields.slice(0, index)}
      index={index}
      groupStartIndex={groupStartIndex}
      groupEndIndex={groupEndIndex}
      hasAttachedQuestion={field.type === 'information' && groupEndIndex > index}
      onAddReaction={handleAddReaction}
      onUpdateField={handleUpdateField}
      onDeleteField={handleDeleteField}
      onDuplicateField={handleDuplicateField}
      canMoveUp={groupStartIndex > 0}
      canMoveDown={groupEndIndex < fields.length - 1}
      onMoveField={handleMoveField}
      onAddField={(position, type, format) => handleAddField(position, type, format, section?.id)}
      onAddSection={sectionsEnabled ? position => handleAddSection(position, section?.id) : undefined}
      onDropField={handleDropField}
      dropLabel={section ? `In ${section.title}` : 'Ungrouped'}
      onGenerateField={(position, description, signal) => handleGenerateField(position, description, signal, section?.id)}
      workspaceId={workspaceId}
      campaignGoal={campaignGoal}
      committedImageKeys={new Set([
        ...committedImageKeys ?? [],
        ...collectQuestionImageStorageKeys(fields.filter(candidate => candidate.id !== field.id)),
      ])}
      surroundingFields={fields.filter(candidate => candidate.id !== field.id && candidate.label.trim().length > 0).map(candidate => ({ label: candidate.label, type: candidate.type }))}
      hasPublishIssue={informationBlockIssues?.has(field.id) ?? false}
      markerHint={informationBlockIssues?.get(field.id)}
      hasCalendarBlock={hasCalendarBlock}
      callsEnabled={callsEnabled}
      fileDestination={fileDestination}
      guestSources={guestSources}
      onGuestBindingChange={handleGuestBindingChange}
    />;
  };

  /** Preserve the existing Information pair presentation inside each section. */
  const renderFields = (items: FormField[], startIndex: number, section?: ConversationSection) => items.map((field, localIndex) => {
    const index = startIndex + localIndex;
    const [start, end] = fieldGroupBounds(fields, index);
    if (start !== index) return null;
    const hasAttachedQuestion = end > index;
    return <div key={field.id} data-motion-key={`field-${field.id}`}
      role={hasAttachedQuestion ? 'group' : undefined}
      aria-label={hasAttachedQuestion ? 'Information and question' : undefined}
      className={hasAttachedQuestion ? 'space-y-3' : undefined}>
      {renderField(field, index, section)}
      {hasAttachedQuestion && <div className="ml-2 border-l-2 border-border pl-4">{renderField(fields[end], end, section)}</div>}
    </div>;
  });
  const blocks = getConversationSectionBlocks(fields, sections);

  // Skeleton placeholders for the streaming reveal.
  const SKELETON_VARIANTS = ['long', 'medium', 'short', 'medium', 'long'] as const;
  const SKELETON_COUNT = 4;

  const SKELETON_PLACEHOLDER_FIELD: FormField = {
    id: '',
    name: '',
    label: '',
    required: false,
    mustOrTry: 'try',
    type: 'free_form_explore',
  };
  const noopUpdate = useCallback(() => {}, []);
  const noopDelete = useCallback(() => {}, []);
  const noopMove = useCallback(() => {}, []);

  const skeletonSlotCount = isGenerating
    ? Math.max(0, SKELETON_COUNT - fields.length)
    : 0;

  const showEmptyPrompt = fields.length === 0 && !sections.length && !isGenerating;

  return (
    <DndProvider backend={HTML5Backend}>
      <div ref={containerRef} className="section-builder mx-auto w-full max-w-3xl">
        {showEmptyPrompt ? (
          // Notion-style empty canvas: a ghost block previewing the per-card
          // "+ ::" left rail (always visible at empty state, since there's no
          // card to hover over) and an inline placeholder. Clicking anywhere
          // on the row creates the first Text answer.
          <div className="space-y-4">
            <div className="group relative">
              <div
                className="absolute -left-12 top-2 hidden items-start gap-0.5 text-muted-foreground sm:flex"
                aria-hidden="true"
              >
                <span className="flex h-6 w-6 items-center justify-center">
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                  </svg>
                </span>
                <span className="flex h-6 w-6 items-center justify-center">
                  <svg viewBox="0 0 10 16" className="h-4 w-2.5" fill="currentColor">
                    <circle cx="2" cy="3" r="1.2" />
                    <circle cx="2" cy="8" r="1.2" />
                    <circle cx="2" cy="13" r="1.2" />
                    <circle cx="8" cy="3" r="1.2" />
                    <circle cx="8" cy="8" r="1.2" />
                    <circle cx="8" cy="13" r="1.2" />
                  </svg>
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleAddField(0)}
                data-testid="form-builder-empty-prompt"
                className="block w-full cursor-text rounded-lg px-2 py-2 text-left text-base text-muted-foreground hover:text-muted-foreground transition-colors"
              >
                Type a question.
              </button>
            </div>
            <AddFieldButton
              insertIndex={0}
              onAddSection={sectionsEnabled ? index => handleAddSection(index) : undefined}
              onAddField={handleAddField}
              onGenerateField={handleGenerateField}
              variant="bottom"
            />
          </div>
        ) : (
          // Generous vertical rhythm between blocks so each question reads
          // as a self-contained unit and the per-card hover rail has room
          // to breathe - tighter spacing made the stack feel like a dense
          // wall of text.
          <div className="space-y-6">
            {/* Keep the block tree stable before the first streamed section;
                switching wrappers would remount already revealed questions. */}
            {sectionsEnabled || sections.length > 0 ? <div className="section-list">{blocks.map((block, blockIndex) => {
              if (block.kind === 'ungrouped') return <div key={`ungrouped-${block.fields[0]?.id ?? blockIndex}`} className="space-y-6">{renderFields(block.fields, block.startIndex)}</div>;
              const section = block.section;
              const sectionIndex = sections.findIndex(candidate => candidate.id === section.id);
              const endIndex = block.startIndex + block.fields.length;
              return <DraggableSection key={section.id} id={section.id} index={sectionIndex} title={section.title} description={section.description} tone={toneFor(section.id)}
                count={block.fields.filter(field => field.type !== 'information').length} expanded={!collapsed.has(section.id)}
                onToggle={() => setCollapsed(previous => { const next = new Set(previous); if (next.has(section.id)) next.delete(section.id); else next.add(section.id); return next; })}
                beforeLayout={capture} afterLayout={play} onReorder={handleMoveSection}
                onMoveQuestion={(fieldId, sectionId, beforeFieldId) => { const next = moveSectionFields(fieldsRef.current, sectionsRef.current, fieldId, sectionId, beforeFieldId); commit(next.fields, next.sections); setCollapsed(previous => new Set([...previous].filter(id => id !== sectionId))); }}
                onTitleChange={title => commit(fieldsRef.current, sectionsRef.current.map(candidate => candidate.id === section.id ? { ...candidate, title } : candidate))}
                onDescriptionChange={description => commit(fieldsRef.current, sectionsRef.current.map(candidate => candidate.id === section.id ? { ...candidate, description } : candidate))}
                conditions={<QuestionConditionEditor field={{ type: 'section', showWhen: section.showWhen }}
                  earlierFields={sectionConditionSources(fields, sections, section.id)}
                  onChange={showWhen => commit(fieldsRef.current, sectionsRef.current.map(candidate => {
                    if (candidate.id !== section.id) return candidate;
                    const { showWhen: _previous, ...metadata } = candidate;
                    return { ...metadata, ...(showWhen ? { showWhen } : {}) };
                  }))} />}
                actions={<BlockActionsMenu trigger={<button type="button" className="icon-button" aria-label={`Actions for ${section.title}`}><IconDots size={16} /></button>}
                  items={[{ label: 'Ungroup', onClick: () => commit(fieldsRef.current, sectionsRef.current.filter(candidate => candidate.id !== section.id)) }]}
                  canMoveUp={sectionIndex > 0} canMoveDown={sectionIndex < sections.length - 1} onMoveUp={() => handleMoveSection(sectionIndex, sectionIndex - 1)} onMoveDown={() => handleMoveSection(sectionIndex, sectionIndex + 1)} />}>
                <div className="space-y-6">{renderFields(block.fields, block.startIndex, section)}</div>
                {!isGenerating && <div className="section-add-field"><AddFieldButton variant="bottom" insertIndex={endIndex}
                  onAddField={(position, type, format) => handleAddField(position, type, format, section.id)}
                  onAddSection={position => handleAddSection(position, section.id)}
                  onGenerateField={(position, description, signal) => handleGenerateField(position, description, signal, section.id)} hasCalendarBlock={hasCalendarBlock} /></div>}
              </DraggableSection>;
            })}</div> : renderFields(fields, 0)}

            {Array.from({ length: skeletonSlotCount }).map((_, i) => {
              const slotIndex = fields.length + i;
              return (
                <FieldCard
                  key={`skel-${slotIndex}`}
                  field={SKELETON_PLACEHOLDER_FIELD}
                  index={slotIndex}
                  onUpdateField={noopUpdate}
                  onDeleteField={noopDelete}
                  onMoveField={noopMove}
                  loading
                  skeletonLabelWidth={
                    SKELETON_VARIANTS[slotIndex % SKELETON_VARIANTS.length]
                  }
                />
              );
            })}

            {/* Bottom append button - round, accent. The only "+" outside the
                per-card hover rails. Hidden while skeletons are still shown so
                the canvas reads as still-loading. */}
            {!isGenerating && (!sections.length || blocks.at(-1)?.kind === 'ungrouped') && (
              <div className="pt-4">
                <AddFieldButton
                  insertIndex={fields.length}
                  onAddSection={sectionsEnabled ? index => handleAddSection(index) : undefined}
                  onAddField={handleAddField}
                  onGenerateField={handleGenerateField}
                  variant="bottom"
                  hasCalendarBlock={hasCalendarBlock}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </DndProvider>
  );
};
